<script setup lang="ts">
import { computed, nextTick, ref } from "vue";
import { addHours, format } from "date-fns";
import ru from "date-fns/locale/ru";
import store from "@/store/index";
import { ExchangeWeb, ExchangeWebUser } from "@/types/beru";
import VitaIcon from "./UI/VitaIcon.vue";

const props = defineProps<{
  exchangeWeb: ExchangeWeb[];
}>();

const emit = defineEmits(["toggle"]);

const timelineExpanded = ref(false);

interface TimelineItem {
  status: string;
  user: string;
  date: string;
  time: string;
  dotClass: string;
  qaLabel: string;
}

const pharmTz = computed<number>(() => store.getters["data/pharmTz"] || 0);

const toPharmDate = (iso: string): Date => {
  const timeZone = pharmTz.value + new Date().getTimezoneOffset() / 60;
  return addHours(new Date(iso), timeZone);
};

// Получаем строку с ФИО и подразделением
const formatUser = (user: ExchangeWebUser): string => {
  const initials = [user.firstName, user.middleName]
    .filter(Boolean)
    .map((name) => `${name[0]}.`)
    .join("");
  const fullName = [user.lastName, initials].filter(Boolean).join(" ");
  const namePodr = user.serviceNumbers?.namePodr;
  return namePodr ? `${fullName} (${namePodr})` : fullName;
};

const timelineItems = computed<TimelineItem[]>(() =>
  props.exchangeWeb.flatMap((exchange) => {
    const statuses = [
      {
        status: "Взята",
        date: exchange.createdAt,
        dotClass: "is-current",
        qaLabel: "taken",
      },
      {
        status: "Согласована",
        date: exchange.confirmedAt,
        dotClass: "is-completed",
        qaLabel: "confirmed",
      },
      {
        status: "Отменена",
        date: exchange.deletedAt,
        dotClass: "",
        qaLabel: "canceled",
      },
    ]
      .filter(
        (
          item,
        ): item is {
          status: string;
          date: string;
          dotClass: string;
          qaLabel: string;
        } => Boolean(item.date),
      )
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    return statuses.map((item) => ({
      status: item.status,
      user: formatUser(exchange.user),
      date: format(toPharmDate(item.date), "d MMMM yyyy", { locale: ru }),
      time: format(toPharmDate(item.date), "HH:mm"),
      dotClass: item.dotClass,
      qaLabel: item.qaLabel,
    }));
  }),
);

const onExpandTimelineClick = async () => {
  timelineExpanded.value = !timelineExpanded.value;
  await nextTick();
  emit("toggle", timelineExpanded.value);
};
</script>
<template>
  <div
    class="timeline"
    :class="{ 'is-expanded': timelineExpanded }"
    v-if="timelineItems.length"
    data-qa="beru-timeline-block"
  >
    <div
      class="timeline-item"
      v-for="(item, idx) in timelineItems"
      :key="idx"
      :data-qa="`beru-timeline-item-${item.qaLabel}-block`"
    >
      <div class="timeline-axis">
        <div
          class="timeline-dot"
          :class="item.dotClass"
          :data-qa="`beru-timeline-item-${item.qaLabel}-dot`"
        ></div>
      </div>
      <div class="timeline-content">
        <div class="timeline-title" :data-qa="`beru-timeline-item-${item.qaLabel}-status`">{{ item.status }}</div>
        <div class="timeline-desc" :data-qa="`beru-timeline-item-${item.qaLabel}-user`">{{ item.user }}</div>
      </div>
      <div class="timeline-date-time">
        <div class="timeline-date" :data-qa="`beru-timeline-item-${item.qaLabel}-date`">{{ item.date }}</div>
        <div class="timeline-time" :data-qa="`beru-timeline-item-${item.qaLabel}-time`">{{ item.time }}</div>
      </div>
    </div>

    <button
      class="timeline-expand"
      v-if="timelineItems.length > 1"
      @click="onExpandTimelineClick"
      data-qa="beru-timeline-button-expand-toggle"
    >
      <vita-icon name="chevron-blue-down"/>
      <span>{{ timelineExpanded ? "Свернуть" : "Показать все статусы" }}</span>
    </button>
  </div>
</template>
<style lang="scss" scoped>
.timeline {
  max-width: 600px;
  margin-top: 20px;

  &:not(:last-child) {
    margin-bottom: 20px;
  }

  &:not(.is-expanded) > div:nth-child(n + 2) {
    display: none;
  }

  &.is-expanded .timeline-expand svg {
    transform: rotate(180deg);
  }
}

.timeline-item:first-child:last-child::before {
    display: none;
}

.timeline-item,
.timeline-action {
  display: grid;
  grid-template-columns: 24px 1fr auto;
  column-gap: 16px;
  position: relative;
}

.timeline-item {
  padding-bottom: 16px;

  &:last-of-type {
    padding-bottom: 20px;
    
    &::before {
      top: 35px;
    }
  }
}

.timeline-item::before {
  content: "";
  position: absolute;
  left: 12px;
  top: 45px;
  bottom: 0;
  width: 1px;
  height: 30%;
  background-color: #e2e8f0;
}

.timeline-axis {
  display: flex;
  justify-content: center;
  padding-top: 14px;
}

.timeline-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  z-index: 2;
  background-color: rgba(203, 207, 210, 1);

  &.is-current {
    background-color: #30a8ff;
  }

  &.is-completed {
    width: 24px;
    height: 24px;
    background-color: #1891ff;
    background-position: center center;
    background-repeat: no-repeat;
    background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxMiAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik0zLjY2MDI1IDguOTU1MDZMMTAuNDY1NyAyLjE0OTY2QzEwLjY2NjIgMS45NDkwOSAxMC45OTU0IDEuOTUxMTQgMTEuMTkzOSAyLjE0OTY2TDExLjg1MSAyLjgwNjcxQzEyLjA0OTUgMy4wMDUyMyAxMi4wNDk1IDMuMzM2NDkgMTEuODUxIDMuNTM0OTNMNS4wNDU2IDEwLjM0MDRDNC44NDcwOSAxMC41Mzg5IDQuNTE3ODcgMTAuNTQxIDQuMzE3MzEgMTAuMzQwNEwzLjY2MDI1IDkuNjgzMzVDMy40NTk2OCA5LjQ4Mjc4IDMuNDU5NjggOS4xNTU2MiAzLjY2MDI1IDguOTU1MDZaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTTEuNTM0OTYgNC44MDcyM0w1LjY4MDUxIDguOTUyNzhDNS44ODEgOS4xNTMzNSA1Ljg3ODY1IDkuNDgyOTQgNS42ODA1MSA5LjY4MTA4TDUuMDIzNDYgMTAuMzM4MUM0LjgyNTMyIDEwLjUzNjIgNC40OTMzIDEwLjUzNjIgNC4yOTUxNiAxMC4zMzgxTDAuMTQ5NjA4IDYuMTkyNThDLTAuMDQ4NTI2NSA1Ljk5NDQ0IC0wLjA1MDg4MzUgNS42NjQ4NSAwLjE0OTYwOCA1LjQ2NDI4TDAuODA2NjYzIDQuODA3MjNDMS4wMDcyMyA0LjYwNjc0IDEuMzM0MzkgNC42MDY3NCAxLjUzNDk2IDQuODA3MjNaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K");
  }
}

.timeline-content {
  display: flex;
  flex-direction: column;
}

.timeline-title {
  font-size: 13px;
  font-weight: 500;
  color: rgba(52, 59, 69, 1);
  line-height: 1.25;
  margin-bottom: 2px;
}

.timeline-desc {
  font-size: 12px;
  font-weight: 500;
  color: #949caf;
  line-height: 1.2;
  max-width: 215px;
}

.timeline-date-time {
  text-align: right;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.timeline-date {
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
  line-height: 1.2;
}

.timeline-time {
  font-size: 12px;
  font-weight: 500;
  color: #949caf;
  line-height: 1.2;
}

.timeline-expand {
  font-family: "Montserrat", sans-serif;
  background: transparent;
  padding: 0;
  border: none;
  appearance: none;
  display: flex;
  align-items: center;
  gap: 16px;
  color: #30a8ff;
  font-weight: 500;
  font-size: 15px;
  line-height: 100%;
  letter-spacing: 0;
  text-align: center;
  cursor: pointer;
  transition: opacity 0.15s ease;
  transform: translateX(1px);

  &:hover {
    opacity: 0.8;
  }
}
</style>
